/**
 * REST device-ops endpoints — DOC1 §11.1.
 *
 *   POST /devices/{id}/setup/verify-password   — unlock customize-setup
 *   PUT  /devices/{id}/config                  — save per-product settings snapshot
 *   POST /devices/{id}/command                 — relay a command over MQTT (shares CmdPayload)
 *   POST /devices/{id}/locate                  — make device blink
 *   POST /devices/{id}/replace                 — restore settings onto a new controller
 *   POST /devices/{id}/transfer-owner          — hand the device to a new owner
 */
import { z } from 'zod';
import { E164MobileSchema } from '../common/mobile.js';
import { WiredSensorSettingSchema, WIRED_SENSOR_MAX, assertUniqueWiredSensorIds, } from '../common/sensors.js';
import { StairModeSchema, CmdPayloadSchema } from '../mqtt/cmd.js';
import { DeviceSchema } from './devices.js';
// ── POST /devices/{id}/setup/verify-password ───────────────────────────────
export const VerifyPasswordBody = z.object({
    setupPassword: z.string().min(1),
});
export const VerifyPasswordResponse = z.object({
    ok: z.literal(true),
});
// ── POST /devices/{id}/setup/forgot-password ───────────────────────────────
// No body. Sends a reset OTP by SMS to the DEVICE OWNER's mobile — never to
// the caller's — so a main_user member can start the flow but only someone
// holding the owner's phone can finish it.
export const SetupPasswordForgotResponse = z.object({
    /** Owner mobile with all but the last 4 digits hidden, e.g. "+91••••••9671". */
    maskedMobile: z.string().min(1),
    /** ISO timestamp after which the OTP may be re-requested. */
    resendAvailableAt: z.string(),
});
// ── POST /devices/{id}/setup/reset-password ────────────────────────────────
export const SetupPasswordResetBody = z.object({
    /** The OTP delivered to the owner's mobile by forgot-password. */
    otp: z.string().min(4).max(8),
    /** Replaces device_identity.setup_password_hash (stored argon2-hashed). */
    newPassword: z.string().min(4).max(64),
});
export const SetupPasswordResetResponse = z.object({
    ok: z.literal(true),
});
// ── PUT /devices/{id}/config ───────────────────────────────────────────────
const Hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
const Brightness = z.number().int().min(0).max(100);
const Iana = z.string().min(1).max(64); // e.g. "Asia/Kolkata"
export const LiftFloorSchema = z.object({
    index: z.number().int(),
    /** Display name (e.g. "Lobby", "Parking"). */
    name: z.string().min(1).max(64),
    /** Predefined voice commands recognised on the phone (DOC1 §11 notes). */
    voiceCommands: z.array(z.string()).default([]),
});
export const LiftConfigSchema = z.object({
    type: z.literal('lift'),
    floorCount: z.number().int().min(1).max(200),
    floors: z.array(LiftFloorSchema).min(1),
    timezone: Iana,
});
// ── Ceiling pattern / switch schemas ──────────────────────────────────────
//
// Shapes agreed in docs/Ceiling_Light_API_Reference.xlsx, sheet "Final Payload
// (Cloud + LAN)". Two rules drive the whole design:
//
//   1. A PATTERN is a device-level object stored on the controller by its own
//      `set_sequence` command and keyed by NAME. It owns the channels; a
//      switch only names it. (Patterns used to be nested inside each switch
//      and keyed by an app-minted `id` — two identifiers for one thing, which
//      is exactly what left `activePatternId` and the pattern's own name able
//      to disagree.)
//   2. A SWITCH is a physical SLOT the controller assigned to a sensor, given
//      a name by the user. It carries no channels and no runtime state: on/off
//      and brightness are live values (set_switch + the device's own status),
//      never part of the saved setup.
/** One step of a ceiling pattern — a single channel's own settings. The step
 *  ORDER is the activation order when the switch running it has
 *  `sequenceEnabled`. Mirrors SetSequenceCmd's step shape (mqtt/cmd.ts), which
 *  is what carries these to the device; `channel` is that command's `stair`. */
export const CeilingPatternStepSchema = z.object({
    /** Controller output channel. The hardware has 24; the contract allows the
     *  same 1-32 span as the stair light so one command serves both. */
    channel: z.number().int().min(1).max(32),
    brightness: Brightness,
    /** How long to wait after this step before the next one. Ignored when the
     *  switch running this pattern has `sequenceEnabled: false`. */
    delayMs: z.number().int().min(0).max(20000),
});
/**
 * One FRAME of a ceiling pattern — the editor's unit, never the wire's.
 *
 * A frame is a group of channels that turn on TOGETHER, and a rule for what
 * those channels do once the NEXT frame turns on. The app compiles a frame
 * list into the flat `steps` the device plays (a step is "set channel to
 * brightness"; brightness 0 is off; delayMs 0 is no wait), so a ripple like
 * "6+7, then 5+8 while 6+7 go dark, …, then all on" needs nothing new on the
 * controller. Frames are stored NEXT TO the compiled steps so the editor can
 * reopen a pattern as the user built it; the backend and firmware read only
 * `steps` (`buildCeilingSequenceCmds` in the backend's device-ops service).
 *
 * Timeline rules the compiler follows (apps/mobile/lib/features/setup/
 * ceiling_frame_compiler.dart is the reference implementation):
 *  - frame i turns on at T[i]; T[0] = 0, T[i+1] = T[i] + frames[i].delayMs;
 *  - `afterNext: 'off'` turns frame i's channels OFF at T[i+1] + afterNextMs
 *    (0 = the instant the next frame turns on); `'keepOn'` leaves them on
 *    until the switch itself is turned off;
 *  - an OFF is dropped when a later frame re-lights that channel at or before
 *    that time (the re-lighting frame owns the channel);
 *  - the LAST frame is the end state: its delayMs/afterNext are stored but
 *    ignored — its channels always stay on.
 */
export const CeilingFrameSchema = z.object({
    /** Channels that turn on together, in tap order. No duplicates. The
     *  hardware has 24; the same 1-32 span as `CeilingPatternStepSchema`. */
    channels: z.array(z.number().int().min(1).max(32)).min(1).max(32),
    /** One level for every channel of the frame. */
    brightness: Brightness,
    /** Time from THIS frame's ON to the NEXT frame's ON. Ignored on the last
     *  frame. Same 20 s cap as a step's delay, so every compiled gap fits. */
    delayMs: z.number().int().min(0).max(20000),
    /** What this frame's channels do once the next frame turns on. */
    afterNext: z.enum(['off', 'keepOn']).default('keepOn'),
    /** For `afterNext: 'off'` — how long after the next frame turns on these
     *  channels go dark. 0 = off now. Ignored for `'keepOn'`. */
    afterNextMs: z.number().int().min(0).max(20000).default(0),
});
/**
 * A device-level lighting pattern. Stored in the saved config so the server
 * can re-push it (device replace, cloud re-apply) and so the app can list it,
 * but it NEVER rides inside `apply_config` — it travels as its own
 * `set_sequence`, same as the stair light's `sequences` (see `toWireSettings`
 * in the backend's device-ops service).
 *
 * `name` is the key: it is what `DeviceSwitchSchema.patternName` references
 * and what the device stores it under. Matched case-insensitively.
 *
 * `steps` is the WIRE truth (what `set_sequence` carries, always present).
 * `frames` is the EDITOR truth (CeilingFrameSchema) — optional so patterns
 * saved before frames existed still validate; the app opens those as one
 * keep-on frame per step, which is exactly the behaviour they had. When
 * both are present, `steps` is `frames` compiled; the app writes them
 * together and nothing else edits either.
 */
export const CeilingPatternSchema = z.object({
    name: z.string().min(1).max(64),
    steps: z.array(CeilingPatternStepSchema).min(1).max(64),
    frames: z.array(CeilingFrameSchema).min(1).max(64).optional(),
});
/**
 * A soft-deleted pattern: removed from the active pool (and from device
 * flash via delete_sequence), but kept restorable until the retention window
 * (app settings `ceilingPatternRetentionDays`, default 30) elapses. The
 * backend purges expired entries on every config save; readers must also
 * filter by `deletedAt` so an expired entry can never resurface.
 */
export const CeilingDeletedPatternSchema = CeilingPatternSchema.extend({
    deletedAt: z.string().datetime({ offset: true }),
});
/**
 * One configured ceiling slot — a sensor/switch the controller reported,
 * named by the user, pointed at a pattern.
 */
export const DeviceSwitchSchema = z.object({
    /** Slot number assigned by the CONTROLLER and reported in its sensor
     *  inventory. The app can only ever configure a slot the device reported;
     *  it is also the key a bus event (a physical press) resolves through. */
    slot: z.number().int().min(1),
    /** User-facing name, and the key APP commands use — `SetSwitchCmd.switchName`
     *  (mqtt/cmd.ts). Unique per device: two slots sharing a name would make a
     *  set_switch ambiguous. A rename therefore travels as a config save, never
     *  as a command. */
    name: z.string().min(1).max(64),
    /** The pattern this slot runs, by CeilingPatternSchema.name. Null while the
     *  slot is configured but has no pattern chosen yet. */
    patternName: z.string().min(1).max(64).nullable(),
    /** true  = play the pattern's channels one at a time in step order,
     *          honouring each step's delayMs — in BOTH directions: turn-on
     *          walks the steps forward, turn-off walks them in REVERSE (last
     *          channel lit goes dark first), mirroring the same delays.
     *  false = switch every channel of the pattern together, delays ignored.
     *  The pattern runs either way — this is the turn on-off STYLE, not an
     *  on/off for the pattern. (It replaces the pattern-level `followSequence`:
     *  style belongs to the slot, so one pattern can run sequenced on one slot
     *  and all-at-once on another.) */
    sequenceEnabled: z.boolean().default(true),
    /** "Running" (ceiling firmware ≥ 1.1.0): with `sequenceEnabled`, the
     *  turn-on walk does not stop at the last step — it pauses for the last
     *  step's delayMs (the firmware floors that at 100 ms) and starts again
     *  from step 0, until the switch is turned off. Turn-off is the usual
     *  reverse walk, begun at the step that fired last, followed by a sweep
     *  of every channel of the pattern to 0. Ignored (not rejected) when
     *  `sequenceEnabled` is false — an all-at-once switch has no walk to
     *  loop. The three app-facing styles are therefore:
     *    Follow sequence = sequenceEnabled + !loop
     *    All at once     = !sequenceEnabled
     *    Running         = sequenceEnabled + loop
     *  Older firmware ignores the key: a Running switch plays once. */
    loop: z.boolean().default(false),
    ldrEnabled: z.boolean().default(false),
    /** LDR hysteresis window on the app-facing 0–10 scale (0 = dark), PER SLOT
     *  against the device's ONE shared LDR: reading below `ldrMin` → this slot
     *  turns ON by itself; reading above `ldrMax` → turns OFF by itself; in
     *  between nothing moves, so a dusk reading hovering on one threshold can
     *  never chatter. Boundary-CROSSING triggered in firmware, so a manual
     *  toggle between the thresholds is respected. Firmware corrects
     *  `ldrMax <= ldrMin` to `ldrMin + 1`. Meaningful only when `ldrEnabled`. */
    ldrMin: z.number().int().min(0).max(10).default(2),
    ldrMax: z.number().int().min(0).max(10).default(5),
    /** Whether this slot's onTime/offTime schedule is active. Per-slot, so each
     *  can be scheduled independently. */
    scheduleEnabled: z.boolean().default(false),
    /** Schedule window, "HH:mm" 24-hour. Explicitly null when the schedule is
     *  off — the field is always present, so a save can never be read as "leave
     *  whatever was there". */
    onTime: Hhmm.nullable().default(null),
    offTime: Hhmm.nullable().default(null),
    /** Per-slot fade: when enabled, every step of this slot's pattern ramps
     *  to its level over `fadePct` % of the step's beat (the time until the
     *  walk fires again — the frame's delay); disabled = instant writes. A
     *  pattern carries no fade of its own — fade belongs to the slot, so one
     *  pattern can fade on one slot and snap on another.
     *
     *  `fadePct` (firmware ≥ 1.4.0): 100 = the ramp fills the whole beat
     *  (smoothest), 10 = near-instant. A ramp always lands before the next
     *  step, so a chase never retargets a half-lit channel, and the delay
     *  itself never changes. Where there is no beat (All at once, a
     *  single-pass walk's last step) 100% = 1 s.
     *
     *  `fadeMs` is the pre-1.4.0 fixed duration, kept only for older
     *  firmware; the app sends `fadePct × 10` (the All-at-once equivalent).
     *  Firmware ≥ 1.4.0 ignores it. */
    fadeEnabled: z.boolean().default(false),
    fadePct: z.number().int().min(0).max(100).default(100),
    fadeMs: z.number().int().min(0).max(3000).default(500),
});
// ── CeilingConfigSchema ────────────────────────────────────────────────────
export const CeilingConfigSchema = z.object({
    type: z.literal('ceiling'),
    timezone: Iana,
    /** Device-wide brightness fallback, used by a slot whose pattern does not
     *  set one. Not edited in Setup — the control screen owns it — so it is
     *  round-tripped rather than dropped. */
    brightness: Brightness.optional(),
    /** Every slot the user has configured. Replace-in-full. */
    switches: z.array(DeviceSwitchSchema).default([]),
    /** The device-level pattern pool. Stored here, but stripped before
     *  `apply_config` goes on the wire — each pattern travels as its own
     *  `set_sequence`, sent FIRST so a pattern is already in flash by the time
     *  a switch names it. */
    patterns: z.array(CeilingPatternSchema).default([]),
    /** Soft-deleted patterns awaiting restore or retention purge. Never sent
     *  to the device (stripped like `patterns`; their flash copy is removed by
     *  delete_sequence at delete time). */
    deletedPatterns: z.array(CeilingDeletedPatternSchema).default([]),
});
/** A single step's own settings — PAT-4: the wire format used to be a bare
 *  stair-number array, silently discarding each step's individual
 *  brightness/delay and flattening the pattern to a single global value. */
export const StairSequenceStepSchema = z.object({
    stairNumber: z.number().int(),
    brightness: Brightness,
    delayMs: z.number().int().min(0),
});
export const StairSequenceSchema = z.object({
    name: z.string().min(1).max(64),
    pattern: z.string().min(1),
    brightness: Brightness,
    /** Ordered lighting steps (DOC1 §7.11). A bare stair-number array is still
     *  accepted from older app builds (each step then falls back to this
     *  sequence's own `brightness` and a 300ms delay), but the app should
     *  always send full step objects now. */
    steps: z.union([
        z.array(StairSequenceStepSchema).min(1),
        z.array(z.number().int()).min(1),
    ]),
    orderIndex: z.number().int().nonnegative(),
});
export const StairConfigSchema = z.object({
    type: z.literal('stair'),
    startStair: z.number().int(),
    endStair: z.number().int(),
    stairCount: z.number().int().min(1),
    mode: StairModeSchema,
    brightness: Brightness,
    pattern: z.string().min(1),
    /** Milliseconds between animation steps (e.g. wave/chase speed). Widened
     *  from the old 0-100 "seconds-ish" scale now that Setup edits this in real
     *  milliseconds with 10ms granularity. */
    patternSpeed: z.number().int().min(0).max(2000),
    /** Fade is an EFFECT, not a pattern (the old built-in 'Fade' pattern chip
     *  is gone): lights ramp instead of switching. Edited in Pattern Settings
     *  below Pattern Speed; firmware key vocabulary is fade/fadetime. */
    fadeEnabled: z.boolean().optional(),
    fadeMs: z.number().int().min(0).max(3000).optional(),
    ldrEnabled: z.boolean(),
    /** Ambient-light window on the app-facing 0–10 scale (0 = dark), against the
     *  ONE ambient-light sensor an M8 controller pairs. Device-wide, unlike
     *  ceiling where the same window is per slot — a staircase is a single
     *  space with a single light level.
     *
     *  ON STAIR THE LDR IS A CONDITION, NOT AN ACTOR. Unlike ceiling, it never
     *  switches anything: it decides whether an automatic SENSOR trigger is
     *  worth running.
     *
     *    reading < `ldrMin`  → dark, a sensor trigger runs the staircase
     *    reading > `ldrMax`  → bright enough already, the trigger is ignored
     *    in between          → whatever the previous answer was
     *
     *  The middle band is why there are two numbers: dusk wanders across a
     *  threshold for twenty minutes rather than crossing it once, and holding
     *  the previous answer through the band means the gate flips twice a day
     *  instead of repeatedly. Firmware corrects `ldrMax <= ldrMin` to
     *  `ldrMin + 1`, same as ceiling.
     *
     *  Gates SENSOR triggers only — app commands and the self-test always run.
     *  Fails OPEN: no `ldr` slot paired, one gone silent, or `ldrEnabled` false
     *  all mean triggers run regardless of the light level. */
    ldrMin: z.number().int().min(0).max(10).default(2),
    ldrMax: z.number().int().min(0).max(10).default(5),
    sequences: z.array(StairSequenceSchema).default([]),
    timezone: Iana,
    /** Stair indices excluded from the lighting sequence (the "Skip Stairs" picker). */
    skippedStairs: z.array(z.number().int()).default([]),
    /** Independent toggle state — `mode` above is the single value sent to the
     *  controller (party > night > guide > auto priority); these flags record
     *  which UI toggles are actually on, since night/guide can combine with the
     *  auto base state while party is fully exclusive. */
    partyMode: z.boolean().optional(),
    nightMode: z.boolean().optional(),
    guideMode: z.boolean().optional(),
    /** The OVERLAY SCHEDULE window, "HH:mm" in the device's `timezone` — ONE
     *  window driving BOTH overlay modes.
     *
     *  At `nightStart` the firmware turns on whichever of Night and Guide are
     *  enabled (`nightMode` / `guideMode` below); at `nightEnd` it drops both
     *  and returns to Auto. The two combine rather than compete — Night sets
     *  the brightness ceiling, Guide adds the standing first/last-stair glow —
     *  so "both enabled" really means both.
     *
     *  The field names still say "night" because they predate Guide joining the
     *  window; renaming them would mean migrating every stored config to
     *  describe the same two times differently, for no behavioural gain.
     *
     *  Edge-triggered on the boundary, so a manual mode change inside the window
     *  is respected until the next crossing (the same rule ceiling's per-slot
     *  schedule uses). Needs a trustworthy wall clock: the controller NTP-syncs
     *  and the backend/app also push [TimeSyncCmd]. Both fields must be present
     *  for the schedule to run; either absent means "no schedule", and the
     *  overlays are manual-latch only. */
    nightStart: Hhmm.optional(),
    nightEnd: Hhmm.optional(),
    nightBrightness: Brightness.optional(),
    guideBrightness: Brightness.optional(),
    offDurationMs: z.number().int().min(0).optional(),
    /** WIRED sensor config — one entry per configured input (id → pattern +
     *  rangeCm + direction, common/sensors.ts). Part of the setup snapshot:
     *  rides inside `apply_config.settings` verbatim (unlike `sequences`) and
     *  is restored by device-replace along with everything else. Defaults to []
     *  so configs saved before wired sensors existed keep validating. */
    sensors: z
        .array(WiredSensorSettingSchema)
        .max(WIRED_SENSOR_MAX)
        .superRefine(assertUniqueWiredSensorIds)
        .default([]),
});
export const DeviceConfigBody = z.intersection(z.discriminatedUnion('type', [LiftConfigSchema, CeilingConfigSchema, StairConfigSchema]), z.object({
    /**
     * The configVersion this edit was based on. When present, the save is
     * rejected with 409 CONFIG_VERSION_CONFLICT if the device's current
     * version has moved on — i.e. another phone saved Setup in the meantime.
     *
     * Optional so callers that legitimately overwrite (a quick pattern switch
     * from the control screen, which isn't editing a form) keep last-write-
     * wins. Omitting it from a Setup screen means silently discarding whatever
     * the other phone saved.
     */
    expectedConfigVersion: z.number().int().positive().optional(),
    /**
     * OFFLINE SAVE — the three fields below ride only on a Setup the phone
     * applied to the controller over LAN while it had no internet (it sat on
     * the controller's own hotspot) and is now syncing.
     *
     * `clientSaveId` — the phone's id for that one save. A retried PUT with
     * the same id returns the version the first attempt created instead of
     * writing a second one, so a sync cut off mid-response is safe to repeat.
     */
    clientSaveId: z.string().uuid().optional(),
    /**
     * When the user pressed Save, by the phone's clock. Its presence switches
     * a version mismatch from "reject" to LATEST SAVE WINS: the save is
     * accepted when it is later than the current config's own save time, else
     * refused with 409 CONFIG_SUPERSEDED (`currentConfigVersion` in the body)
     * and the phone puts the controller back on the server's config. With no
     * base version to send, omit `expectedConfigVersion` — any existing
     * config then counts as a mismatch.
     */
    clientSavedAt: z.string().datetime({ offset: true }).optional(),
    /**
     * The phone's clock at the moment of THIS request. The server only uses
     * the difference `clientSentAt − clientSavedAt` (how long ago the save
     * happened) against its own clock, so a phone whose clock is simply wrong
     * still orders correctly. Omitted → `clientSavedAt` is taken at face
     * value, clamped to now.
     */
    clientSentAt: z.string().datetime({ offset: true }).optional(),
}));
export const DeviceConfigResponse = z.object({
    configVersion: z.number().int().positive(),
    isCurrent: z.literal(true),
    /**
     * When this version was saved, by the server's reckoning (for an offline
     * save: the skew-corrected time the user pressed Save). The app passes it
     * on as `apply_config.savedAt` when it applies the setup over LAN, so the
     * controller stamps the same time the cloud apply would have carried.
     */
    savedAt: z.string().datetime({ offset: true }).optional(),
});
// ── POST /devices/{id}/command ─────────────────────────────────────────────
/**
 * Same shape as the MQTT cmd payload, plus REST-only transport hints. The
 * middleware accepts the payload over REST and republishes it on
 * `V1/HARON/{PRODUCT}/{serial}/cmd` with the same cmdId+ts.
 *
 * The hints are an intersection rather than fields on CmdPayloadSchema so they
 * stay out of the MQTT contract — they describe how the command reached us,
 * which is meaningless to a controller. (They could not reach one anyway: the
 * middleware re-validates against CmdPayloadSchema, which strips them.)
 */
export const DeviceCommandBody = z.intersection(CmdPayloadSchema, z.object({
    /**
     * The app already delivered this command to the device directly over the
     * LAN; don't send it again, just record and broadcast the resulting state.
     *
     * Without this, a command sent over the local network is invisible to the
     * cloud — so every OTHER phone keeps showing the old value indefinitely,
     * because nothing ever told the server the device changed.
     */
    alreadyAppliedLocally: z.boolean().optional(),
}));
export const DeviceCommandResponse = z.object({
    cmdId: z.string().min(1).max(64),
    accepted: z.literal(true),
    /** 'sent' = published to broker now; 'queued' = device offline, held until it reconnects. */
    state: z.enum(['sent', 'queued']),
});
// ── POST /devices/{id}/locate ──────────────────────────────────────────────
export const LocateBody = z.object({
    durationSec: z.number().int().min(1).max(60).optional(),
});
// ── POST /devices/{id}/replace ─────────────────────────────────────────────
export const ReplaceBody = z.object({
    newSerialNumber: z.string().min(1).max(64),
    /** Secret Key of the replacement unit. */
    secretKey: z.string().min(1),
});
export const ReplaceResponse = z.object({
    device: DeviceSchema,
    /** The config snapshot version that was pushed to the new controller. */
    restoredConfigVersion: z.number().int().positive().nullable(),
});
// ── POST /devices/{id}/transfer-owner ──────────────────────────────────────
export const TransferOwnerBody = z.object({
    /** The new owner's mobile — they may not yet have an account. */
    newOwnerMobile: E164MobileSchema,
});
export const TransferOwnerResponse = z.object({
    device: DeviceSchema,
    /** Whether existing shares were revoked as part of the transfer. */
    sharesRevoked: z.number().int().nonnegative(),
});
//# sourceMappingURL=device-ops.js.map